import { useCallback, useRef, useState } from 'react';
import { FileText, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ReceiptImageViewerProps {
  src: string;
  alt: string;
  mimeType: string;
  className?: string;
  onImageLoad?: React.ReactEventHandler<HTMLImageElement>;
  onImageError?: React.ReactEventHandler<HTMLImageElement>;
  imageTestId?: string;
  zoomInTestId?: string;
  zoomOutTestId?: string;
  zoomResetTestId?: string;
}

export default function ReceiptImageViewer({
  src,
  alt,
  mimeType,
  className,
  onImageLoad,
  onImageError,
  imageTestId = 'receipt-image-viewer-image',
  zoomInTestId = 'zoom-in',
  zoomOutTestId = 'zoom-out',
  zoomResetTestId = 'zoom-reset',
}: ReceiptImageViewerProps) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTouchDistance = useRef<number | null>(null);

  const isPdf = mimeType === 'application/pdf';

  const handleZoomIn = useCallback(() => {
    setScale((s) => Math.min(s * 1.3, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((s) => {
      const next = s / 1.3;
      if (next <= 1) {
        setTranslate({ x: 0, y: 0 });
        return 1;
      }
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setScale((s) => Math.min(s * 1.1, 5));
    } else {
      setScale((s) => {
        const next = s / 1.1;
        if (next <= 1) {
          setTranslate({ x: 0, y: 0 });
          return 1;
        }
        return next;
      });
    }
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    translateStart.current = { ...translate };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [scale, translate]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || scale <= 1) return;
    setTranslate({
      x: translateStart.current.x + (e.clientX - dragStart.current.x),
      y: translateStart.current.y + (e.clientY - dragStart.current.y),
    });
  }, [isDragging, scale]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Pinch-to-zoom for mobile
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (lastTouchDistance.current !== null) {
        const ratio = distance / lastTouchDistance.current;
        setScale((s) => Math.max(1, Math.min(s * ratio, 5)));
      }
      lastTouchDistance.current = distance;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    lastTouchDistance.current = null;
  }, []);

  // Double-click/tap to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      handleReset();
    } else {
      setScale(2.5);
    }
  }, [scale, handleReset]);

  if (isPdf) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed bg-muted/30 p-8', className)}>
        <FileText className="h-16 w-16 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">PDF Receipt</p>
        <Button variant="outline" asChild>
          <a href={src} target="_blank" rel="noopener noreferrer">
            Open PDF
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('relative flex flex-col', className)}>
      <div
        ref={containerRef}
        className="relative flex-1 overflow-hidden rounded-lg bg-muted/30 touch-none"
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
      >
        <img
          src={src}
          alt={alt}
          onLoad={onImageLoad}
          onError={onImageError}
          data-testid={imageTestId}
          className="h-full w-full object-contain select-none"
          draggable={false}
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.2s ease',
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-center gap-1">
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleZoomOut} data-testid={zoomOutTestId}>
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="min-w-[3rem] text-center text-xs text-muted-foreground">{Math.round(scale * 100)}%</span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleZoomIn} data-testid={zoomInTestId}>
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleReset} data-testid={zoomResetTestId}>
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
